import { Form } from "antd";
import Search from "antd/es/input/Search";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Product, SortOrder } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import BaseButton from "@/components/BaseButton";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import Meta from "@/components/Meta";
import { HeaderFilterButton } from "@/components/UI/HeaderFilterButton";
import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useQueryFilter } from "@/hooks/useQueryFilter";
import { CheckoutProductCard } from "@/pages/Checkout/CheckoutAddProducts/components/CheckoutProductCard/CheckoutProductCard";
import { useSaveOrder } from "@/pages/Checkout/hooks/indes";
import { getFilterCount } from "@/pages/WorkPlan/Tasks/utils";
import {
    setProductsSelectedFilters,
    setProductsSortOrder,
    setProductsSortTag,
    setUpdateProductBalancesQuery,
} from "@/store/slices/ui";

import { OfflineFilterDrawer } from "../../WorkPlan/Tasks/components/OfflineFilterDrawer";

interface LocalProduct {
    barcode: string;
    name: string;
    price: number;
    count: number;
}

export const CheckoutAddProducts = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { marketId, orderId } = useParams();
    const { handleSaveOrder } = useSaveOrder();

    const {
        updateProductBalances,
        productsFilter: { selectedFilters, hideZeroProducts },
    } = useAppSelector((s) => s.ui);
    const { checkoutList, markets } = useAppSelector((s) => s.work);
    const {
        products,
        marketsPriceLists,
        priceLists,
        inventoryList,
        ignoreRemainingsOrders,
    } = useAppSelector((s) => s.offline);

    const checkoutData = useMemo(
        () => (orderId ? checkoutList?.[orderId] : undefined),
        [checkoutList, orderId],
    );

    const [sortedAndFilteredProducts, setSortedAndFilteredProducts] = useState<
        Product[]
    >(() => Object.values(products));
    const [open, setOpen] = useState(false);
    const [localProducts, setLocalProducts] = useState<
        Record<string, LocalProduct>
    >(() => checkoutData.products ?? {});

    const pageTitle = "Додати товар";
    const searchQuery = updateProductBalances.query;

    const searchFilteredProducts = useQueryFilter<Product>(
        sortedAndFilteredProducts,
        searchQuery,
        [
            (p) => p?.name,
            (p) => p?.barcode,
            (p) => p?.params?.description?.value,
            (p) => p?.params?.capacity?.value,
        ],
    );

    const currentPriceList = useMemo(
        () =>
            marketId ? priceLists?.[marketsPriceLists?.[marketId]] : undefined,
        [priceLists, marketsPriceLists, marketId],
    );

    const currentMarket = useMemo(
        () => markets?.[marketId],
        [markets, marketId],
    );
    const currentInventoryList =
        inventoryList?.[currentMarket?.storage?.id ?? ""] || {};

    const filterCount = useMemo(
        () => getFilterCount(selectedFilters),
        [selectedFilters],
    );

    const hasChanges = useMemo(
        () => !isEqual(localProducts, checkoutData?.products ?? {}),
        [localProducts, checkoutData?.products],
    );

    const showDrawer = useCallback(() => setOpen(true), []);

    const handleSearch = useCallback(
        (evt: React.ChangeEvent<HTMLInputElement>) =>
            dispatch(setUpdateProductBalancesQuery(evt.target.value)),
        [dispatch],
    );

    const handleAddProductLocal = useCallback(
        (barcode: string, name: string, price: number) => {
            setLocalProducts((prev) => ({
                ...prev,
                [barcode]: {
                    barcode,
                    name,
                    price,
                    count: 1,
                },
            }));
        },
        [],
    );

    const handleProductCountChangeLocal = useCallback(
        (barcode: string, newCount: number) => {
            setLocalProducts((prev) => {
                if (newCount === 0) {
                    const updated = { ...prev };
                    delete updated[barcode];
                    return updated;
                }

                return {
                    ...prev,
                    [barcode]: {
                        ...prev[barcode],
                        count: newCount,
                    },
                };
            });
        },
        [],
    );

    const [handleSaveChanges, saveLoading] = useAsyncAction(async () => {
        if (!orderId) return;

        await handleSaveOrder({
            orderId,
            products: localProducts,
            storageId: currentMarket?.storage?.id,
        });

        navigate(buildRoutePath(ROUTES.CheckoutOrderDetails, { orderId }), {
            replace: true,
        });
    });

    useEffect(() => {
        dispatch(setProductsSelectedFilters({}));
        dispatch(setProductsSortOrder(SortOrder.Asc));
        dispatch(setProductsSortTag(null));
    }, [dispatch, products]);

    const { visibleItems, loadMore } = useInfiniteScroll<Product>(
        searchFilteredProducts,
    );
    const lastIndex = visibleItems.length - 1;

    return (
        <>
            <Meta title={pageTitle} />

            <Header
                title={pageTitle}
                back
                needConfirm={hasChanges}
                rightActions={
                    <HeaderFilterButton
                        filterCount={filterCount}
                        onClick={showDrawer}
                        active={hideZeroProducts}
                    />
                }
            />

            <MainContainer>
                <Search
                    placeholder="Пошук"
                    defaultValue={searchQuery}
                    onChange={handleSearch}
                    allowClear
                    size="large"
                    style={{ marginBottom: 8 }}
                />

                <div style={{ height: "100%", overflowY: "auto" }}>
                    <Form>
                        {visibleItems.map((item, i) => {
                            const { barcode, name } = item;
                            const itemsLeft = currentInventoryList?.[barcode];
                            const price = currentPriceList?.[barcode]?.price;

                            const localProduct = localProducts[barcode];
                            const checkout = localProduct
                                ? {
                                      barcode: localProduct.barcode,
                                      name: localProduct.name,
                                      count: localProduct.count,
                                      price: localProduct.price,
                                      marketId: marketId!,
                                      apiCount:
                                          checkoutData?.products?.[barcode]
                                              ?.apiCount ?? 0,
                                  }
                                : undefined;

                            return (
                                <CheckoutProductCard
                                    key={barcode}
                                    barcode={barcode}
                                    checkout={checkout}
                                    name={name}
                                    itemsLeft={itemsLeft}
                                    price={price ?? 0}
                                    orderId={orderId!}
                                    ignoreRemainingsOrders={
                                        ignoreRemainingsOrders
                                    }
                                    isLast={i === lastIndex}
                                    onView={loadMore}
                                    onAddProductLocal={handleAddProductLocal}
                                    onProductCountChangeLocal={
                                        handleProductCountChangeLocal
                                    }
                                />
                            );
                        })}
                    </Form>
                </div>

                {hasChanges && (
                    <BaseButton
                        onClick={handleSaveChanges}
                        type="primary"
                        style={{ marginTop: 8 }}
                        disabled={!hasChanges}
                        loading={saveLoading}
                    >
                        Зберегти зміни
                    </BaseButton>
                )}
            </MainContainer>

            <OfflineFilterDrawer
                open={open}
                setOpen={setOpen}
                inventoryList={currentInventoryList}
                setFilteredProducts={setSortedAndFilteredProducts}
            />
        </>
    );
};
